/** The console's pages, their deep links, the words on the "Soon" pages,
 * and the client filter's logic. No DOM here -- pure functions, tested
 * directly with Node (web/console/test/). Copy follows the voice guide
 * (assets/brand/VOICE-AND-MESSAGING.md): short, plain, to "you", no dates
 * and no feature claims for anything not built. */

export interface AgentInfo {
  slug: string;
  name: string;
  description: string;
}

/** The agents Preekie is working towards. Each is a "Soon" page today. */
export const AGENTS: readonly AgentInfo[] = [
  { slug: "month-end", name: "Month-end", description: "Checks your month is ready to close and shows what's left to do." },
  {
    slug: "internal-audit",
    name: "Internal Audit",
    description:
      "Checks every transaction against your controls and explains each exception, so you can follow it up with the business.",
  },
  {
    slug: "external-audit",
    name: "External Audit",
    description: "Prepares working papers and evidence for your registered auditor.",
  },
  {
    slug: "r-and-d",
    name: "R&D",
    description: "Captures R&D evidence as the work happens, ready for your registered tax agent.",
  },
  { slug: "tax", name: "Tax", description: "Prepares working papers for your registered tax agent." },
  { slug: "ai-governance", name: "AI Governance", description: "See how AI is used across your team." },
];

/** Admins only (account_admin, super_admin). */
export const ONBOARDING: AgentInfo = {
  slug: "onboarding",
  name: "Onboarding",
  description:
    "Connect a new client's Xero, set up their lodgement pattern and data agreement, and see their first health check.",
};

export const COPY = {
  soonPill: "Soon",
  comingSoonPill: "Coming soon",
  tellUs: "Tell us what you'd want from this",
  deadlinesFootnote:
    "Dates follow the lodgement pattern set for each client. Your registered agent's lodgement program dates may differ.",
  notAvailableYet: "Not available yet.",
  profileHelp:
    "To change your password or authenticator, email hello@preekie.com. We verify it is you before resetting anything.",
  notFoundTitle: "Page not found",
  notFoundBody: "This page doesn't exist.",
} as const;

export type View =
  | { kind: "follow-up" }
  | { kind: "deadlines" }
  | { kind: "settings" }
  | { kind: "onboarding" }
  | { kind: "agent"; agent: AgentInfo }
  | { kind: "meetings" }
  | { kind: "record"; client: string | null } // the device recorder's start screen (R3)
  | { kind: "clients" }
  | { kind: "client"; slug: string; tab: "overview" | "meetings" }
  | { kind: "meeting-summary"; meetingId: string }
  | { kind: "meeting-room"; meetingId: string } // a signed-in member (the host's "Copy link")
  | { kind: "guest"; meetingId: string; token: string } // a guest's own link; no Preekie account
  | { kind: "not-found" };

const MEETING_ID = "[0-9a-f]{32}";
const GUEST_TOKEN = "[A-Za-z0-9_-]{20,64}";

export interface Route {
  view: View;
  clients: string[]; // as written in the URL -- sanitise against the caller's scope before use
}

export function parseRoute(pathname: string, search: string): Route {
  const path = pathname.replace(/\/+$/, "") || "/";
  let view: View;
  if (path === "/" || path === "/index.html" || path === "/follow-up") view = { kind: "follow-up" };
  else if (path === "/deadlines") view = { kind: "deadlines" };
  else if (path === "/settings") view = { kind: "settings" };
  else if (path === "/onboarding") view = { kind: "onboarding" };
  else if (path.startsWith("/agents/")) {
    const agent = AGENTS.find((a) => `/agents/${a.slug}` === path);
    view = agent ? { kind: "agent", agent } : { kind: "not-found" };
  } else if (path === "/meetings") view = { kind: "meetings" };
  else if (path === "/meetings/record") {
    const c = new URLSearchParams(search).get("client");
    view = { kind: "record", client: c && /^[a-z0-9-]{1,64}$/.test(c) ? c : null };
  } else if (path === "/clients") view = { kind: "clients" };
  else if (/^\/clients\/[a-z0-9-]{1,64}(\/meetings)?$/.test(path)) {
    const [, , slug, tab] = path.split("/");
    view = { kind: "client", slug: slug!, tab: tab === "meetings" ? "meetings" : "overview" };
  }
  else if (new RegExp(`^/meetings/(${MEETING_ID})/summary$`).test(path))
    view = { kind: "meeting-summary", meetingId: path.split("/")[2]! };
  else if (new RegExp(`^/m/(${MEETING_ID})$`).test(path)) view = { kind: "meeting-room", meetingId: path.split("/")[2]! };
  else if (new RegExp(`^/m/(${MEETING_ID})/(${GUEST_TOKEN})$`).test(path)) {
    const [, , meetingId, token] = path.split("/");
    view = { kind: "guest", meetingId: meetingId!, token: token! };
  } else view = { kind: "not-found" };
  return { view, clients: parseClients(search) };
}

export function parseClients(search: string): string[] {
  const raw = new URLSearchParams(search).getAll("clients").join(",");
  const out: string[] = [];
  for (const slug of raw.split(",")) {
    const s = slug.trim();
    if (/^[a-z0-9-]{1,64}$/.test(s) && !out.includes(s)) out.push(s);
  }
  return out;
}

export function routePath(view: View): string {
  switch (view.kind) {
    case "follow-up":
      return "/follow-up";
    case "deadlines":
      return "/deadlines";
    case "settings":
      return "/settings";
    case "onboarding":
      return "/onboarding";
    case "agent":
      return `/agents/${view.agent.slug}`;
    case "meetings":
      return "/meetings";
    case "record":
      return view.client ? `/meetings/record?client=${view.client}` : "/meetings/record";
    case "clients":
      return "/clients";
    case "client":
      return view.tab === "meetings" ? `/clients/${view.slug}/meetings` : `/clients/${view.slug}`;
    case "meeting-summary":
      return `/meetings/${view.meetingId}/summary`;
    case "meeting-room":
      return `/m/${view.meetingId}`;
    case "guest":
      return `/m/${view.meetingId}/${view.token}`;
    case "not-found":
      return "/follow-up";
  }
}

/** Meetings pages are unknown pages unless the environment has Meetings on
 * (the server's /api/me meetings_enabled; cdk.json meetingsEnabled). */
export function isMeetingsView(view: View): boolean {
  return view.kind === "meetings" || view.kind === "record" || view.kind === "clients" || view.kind === "client" || view.kind === "meeting-summary" || view.kind === "meeting-room" || view.kind === "guest";
}

/** The address bar for a view and a selection. Commas stay readable. */
export function buildUrl(view: View, clients: readonly string[]): string {
  const path = routePath(view);
  if (clients.length === 0) return path;
  return `${path}${path.includes("?") ? "&" : "?"}clients=${clients.join(",")}`;
}

/** A stable id per view: the rail's active item, and the `screen` sent
 * with feedback (must match services/feedback/store.py's pattern). */
export function viewKey(view: View): string {
  if (view.kind === "agent") return `agent-${view.agent.slug}`;
  return view.kind;
}

export function pageTitle(view: View): string {
  switch (view.kind) {
    case "follow-up":
      return "Follow-up";
    case "deadlines":
      return "Deadlines";
    case "settings":
      return "Settings";
    case "onboarding":
      return ONBOARDING.name;
    case "agent":
      return view.agent.name;
    case "meetings":
      return "Meetings";
    case "record":
      return "Record a meeting";
    case "clients":
      return "Clients";
    case "client":
      return "Client";
    case "meeting-summary":
      return "Meeting notes";
    case "meeting-room":
    case "guest":
      return "Meeting";
    case "not-found":
      return COPY.notFoundTitle;
  }
}

/** Onboarding is admins only; for anyone else it behaves exactly like a
 * page that doesn't exist (the server gives the same 404 on a direct
 * load). */
export function visibleView(view: View, canViewOnboarding: boolean, meetingsEnabled = false, chimeEnabled = false): View {
  if (view.kind === "onboarding" && !canViewOnboarding) return { kind: "not-found" };
  if (isMeetingsView(view) && !meetingsEnabled) return { kind: "not-found" };
  // The Chime room's pages: only where its own switch is on too (off in prod; the server 404s them the same way).
  if ((view.kind === "meeting-room" || view.kind === "guest") && !chimeEnabled) return { kind: "not-found" };
  return view;
}

// ---- Client filter ----

export interface FilterOption {
  client_slug: string;
  client_name: string;
}

/** Keeps only slugs this person can see, once each, in the options'
 * order. Anything else from the URL is dropped quietly -- the server
 * ignores it too, so nothing reveals whether it exists. */
export function sanitizeClients(requested: readonly string[], options: readonly FilterOption[]): string[] {
  return options.map((o) => o.client_slug).filter((slug) => requested.includes(slug));
}

export function toggleClient(
  selected: readonly string[],
  slug: string,
  options: readonly FilterOption[]
): string[] {
  const next = selected.includes(slug) ? selected.filter((s) => s !== slug) : [...selected, slug];
  return sanitizeClients(next, options);
}

/** "All (2)", the one client's name, or "3 selected". None selected
 * means all. */
export function filterButtonLabel(selected: readonly string[], options: readonly FilterOption[]): string {
  if (selected.length === 0) return `All (${options.length})`;
  if (selected.length === 1) {
    return options.find((o) => o.client_slug === selected[0])?.client_name ?? `All (${options.length})`;
  }
  return `${selected.length} selected`;
}

export function selectionSummary(selected: readonly string[], options: readonly FilterOption[]): string {
  return `${selected.length} of ${options.length} selected`;
}

export function matchesSearch(name: string, query: string): boolean {
  return name.toLowerCase().includes(query.trim().toLowerCase());
}
